import { apiCall } from '../../services/apiUtil';

/**
 * Resolves a tournament match participant into the player(s) the prediction
 * API needs. Individual participants already carry a user UUID as their id;
 * team participants (doubles) need their 2-member roster fetched.
 *
 * @param {string} type - game.participant_a_type / participant_b_type
 * @param {string|number} id - game.participant_a_id / participant_b_id
 * @param {string} name - game.participant_a_name / participant_b_name (used for non-team participants)
 * @param {Map<string|number, Promise>} [cache] - shared across a batch of matches so the
 *   same team's roster (many matches reuse the same doubles pair) is fetched only once.
 */
export async function resolveParticipant(type, id, name, cache) {
  if (type !== 'team') {
    return { primary: { id, name }, partner: null };
  }

  if (cache?.has(id)) {
    return cache.get(id);
  }

  const promise = (async () => {
    const team = await apiCall(`/teams/${id}`);
    const members = (team.members || []).map(m => ({
      id: m.user.id,
      name: `${m.user.firstName} ${m.user.lastName}`.trim(),
    }));
    if (members.length !== 2) {
      const err = new Error('Prediction only supports 1v1 or 2v2 matches.');
      err.code = 'UNSUPPORTED_FORMAT';
      throw err;
    }
    return { primary: members[0], partner: members[1] };
  })();

  cache?.set(id, promise);
  return promise;
}
